import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetFilterSourceResponse,
  PlatformOptions,
  SourceOptions
} from '@blisspointmedia/bpm-types/dist/App2Performance';
import {
  getDefaultCompareMetrics,
  getDefaultDeliveryOverviewMetrics,
  getDefaultSparkChartMetrics,
  getDeliveryPageDimensionMap,
  getDeliveryPageMetricColumns,
  GetPageDataResponse,
  getSparkChartConfig
} from '@blisspointmedia/bpm-types/dist/Columns';
import {
  DeletePresetParams as DeletePagePresetParams,
  GetPresetsParams as GetPagePresetsParams,
  MetricsPagePreset,
  SavePresetParams as SavePagePresetParams
} from '@blisspointmedia/bpm-types/dist/MetricsPage';
import {
  DeletePresetParams as DeleteTablePresetParams,
  DimensionColumn,
  GetPresetParams as GetTablePresetParams,
  MetricsTablePreset,
  SavePresetParams as SaveTablePresetParams
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import * as P from '@blisspointmedia/bpm-types/dist/Performance';
import {
  App2Channels,
  channelShowsMotionReplacement,
  AUDIO,
  COMMERCE,
  DISPLAY,
  EMAIL,
  LINEAR,
  PROGRAMMATIC,
  RADIO,
  SEARCHSHOPPING,
  SOCIAL,
  STREAMING,
  YOUTUBE
} from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { FullPageSpinner } from '@Components/Spinner';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useSetError } from '@src/redux/modals';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { awaitJSON, SingleChannelLambdaFetch } from '@src/utils/fetch-utils';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { overrideDateRange } from '@src/utils/test-account-utils';
import { DateRange, parseBase64GzippedResponse } from '@src/utils/date-utils';
import { AdInfoRow, DimensionMap } from '@Components/MetricsTable/metrics-table-utils';
import { SingleChannelPage } from '../SingleChannelPage';
import { SourceType, deriveSourceType } from '../../CrossChannel/Components/SourcePicker';
import { getChannelGlossary } from './glossary';
import {
  getDataColMetaData,
  getDimensionCell,
  forceAmazonKpiSettings,
  getDimensionColMetaData
} from './shared-performance-utils';

interface SingleChannelFactoryProps {
  channel: App2Channels;
  isGraph?: boolean;
}

export const SingleChannelFactory = ({ channel, isGraph }: SingleChannelFactoryProps) => {
  const [dates, setDates] = useState<DateRange>();
  const [fetchingPagePresets, setFetchingPagePresets] = useState(false);
  const [fetchingTablePresets, setFetchingTablePresets] = useState(false);
  const [filterData, setFilterData] = useState<P.GetFilterOptionsResponse>();
  const [pagePresets, setPagePresets] = useState<MetricsPagePreset[]>();
  const [tablePresets, setTablePresets] = useState<MetricsTablePreset[]>();
  const company = useLocationCompany() ?? '';
  const setError = useSetError();
  const [sparkMetrics, sparkPrettyNames] = useMemo(() => getSparkChartConfig(channel), [channel]);
  const { fiscal_cal: fiscalCal, currency_code: currencyCode } = useCompanyInfo();
  const columnMetaDataMap = useMemo(
    () => getDataColMetaData(channel, currencyCode),
    [channel, currencyCode]
  );
  const dimensionColumnMetaDataMap = useMemo(() => getDimensionColMetaData(channel), [channel]);
  const deliveryDimensionMap = useMemo(
    () =>
      getDeliveryPageDimensionMap(channel).reduce<Record<string, string>>((acc, dim) => {
        acc[dim.prettyName] = dim.name;
        return acc;
      }, {}),
    [channel]
  );
  const glossary = useMemo(() => getChannelGlossary(channel), [channel]);
  const [sourceOptions, setSourceOptions] = useState<SourceOptions>([]);
  const [platformOptions, setPlatformOptions] = useState<PlatformOptions>([]);
  const [adInfoMap, setAdInfoMap] = useState<Record<string, AdInfoRow>>({});
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const analyticsIsSource = useMemo(
    () => selectedSources.length > 0 && deriveSourceType(selectedSources) === SourceType.ANALYTICS,
    [selectedSources]
  );
  const shouldEnableMotionReplacement = useExperimentFlag('enableMotionReplacement');

  useEffect(() => {
    if (shouldEnableMotionReplacement && company && channelShowsMotionReplacement(channel)) {
      (async () => {
        try {
          const res = await SingleChannelLambdaFetch('/getAdInfo', {
            method: 'GET',
            params: { company }
          });
          const adInfoRows = await awaitJSON(res);
          const newAdInfoMap = adInfoRows.reduce((acc, row) => {
            acc[row.ad_name] = row;
            return acc;
          }, {});
          setAdInfoMap(newAdInfoMap);
        } catch (e) {
          setAdInfoMap({});
          setError({ title: "Couldn't load ad info", message: e.message, reportError: e });
        }
      })();
    }
  }, [company, channel, setError, shouldEnableMotionReplacement]);

  const getDimensionCellWithContext = useCallback(
    (dimensionData: DimensionMap, dimensionHeader: DimensionColumn) =>
      getDimensionCell(
        dimensionData,
        dimensionHeader,
        fiscalCal,
        shouldEnableMotionReplacement ? adInfoMap : undefined,
        company
      ),
    [fiscalCal, adInfoMap, company, shouldEnableMotionReplacement]
  );

  const getPagePresets = useCallback(() => {
    (async () => {
      try {
        setFetchingPagePresets(true);
        const res = await SingleChannelLambdaFetch<GetPagePresetsParams>('/metrics_page_presets', {
          params: {
            company,
            mediatype: channel
          }
        });
        const parsedPresets = await awaitJSON(res);
        if (!parsedPresets.errorMessage) {
          const typedPrests = parsedPresets as MetricsPagePreset[];
          setPagePresets(typedPrests);
        } else {
          setError({
            message: `Failed to fetch page presets. Error: ${parsedPresets.errorMessage}`
          });
        }
      } catch (e) {
        setError({
          message: `Failed to fetch page presets. Error: ${e.message}`,
          reportError: e
        });
      } finally {
        setFetchingPagePresets(false);
      }
    })();
  }, [company, setError, channel]);

  const getTablePresets = useCallback(() => {
    (async () => {
      try {
        setFetchingTablePresets(true);
        const res = await SingleChannelLambdaFetch<GetPagePresetsParams>('/metrics_table_presets', {
          params: {
            company,
            mediatype: channel
          }
        });
        const parsedPresets = await awaitJSON(res);
        if (!parsedPresets.errorMessage) {
          setTablePresets(parsedPresets as MetricsTablePreset[]);
        } else {
          setError({
            message: `Failed to fetch table presets. Error: ${parsedPresets.errorMessage}`
          });
        }
      } catch (e) {
        setError({
          message: `Failed to fetch table presets. Error: ${e.message}`,
          reportError: e
        });
      } finally {
        setFetchingTablePresets(false);
      }
    })();
  }, [company, setError, channel]);

  const getTablePreset = useCallback(
    (params: GetTablePresetParams) =>
      (async () => {
        try {
          const res = await SingleChannelLambdaFetch<GetTablePresetParams>(
            '/metrics_table_preset',
            {
              params
            }
          );
          return await awaitJSON<MetricsTablePreset>(res);
        } catch (e) {
          setError({
            message: `Failed to fetch table preset. Error: ${e.message}`,
            reportError: e
          });
          return undefined;
        }
      })(),
    [setError]
  );

  const savePagePreset = useCallback(
    (body: Partial<SavePagePresetParams>) =>
      (async () => {
        let presetResponse;
        try {
          setFetchingPagePresets(true);
          const res = await SingleChannelLambdaFetch<SavePagePresetParams>('/metrics_page_preset', {
            method: 'POST',
            body: body as SavePagePresetParams
          });
          presetResponse = await awaitJSON<P.SavePresetResponse>(res);
          return presetResponse;
        } catch (e) {
          setError({
            message: `Failed to save page preset. Error: ${e.message}`,
            reportError: e
          });
          presetResponse = { message: `Failed to save page preset. Error: ${e.message}` };
          return presetResponse;
        } finally {
          getPagePresets();
        }
      })(),
    [getPagePresets, setError]
  );

  const saveTablePreset = useCallback(
    (body: Partial<SaveTablePresetParams>) =>
      (async () => {
        let presetResponse;
        try {
          setFetchingTablePresets(true);
          const res = await SingleChannelLambdaFetch<SaveTablePresetParams>(
            '/metrics_table_preset',
            {
              method: 'POST',
              body: body as SaveTablePresetParams
            }
          );
          presetResponse = await awaitJSON<P.SavePresetResponse>(res);
          return presetResponse;
        } catch (e) {
          setError({
            message: `Failed to save table preset. Error: ${e.message}`,
            reportError: e
          });
          return undefined;
        } finally {
          getTablePresets();
        }
      })(),
    [getTablePresets, setError]
  );

  const deletePagePreset = useCallback(
    (params: DeletePagePresetParams) =>
      (async () => {
        try {
          setFetchingPagePresets(true);
          const res = await SingleChannelLambdaFetch<DeletePagePresetParams>(
            '/metrics_page_preset',
            {
              method: 'DELETE',
              params
            }
          );
          await awaitJSON(res);
        } catch (e) {
          setError({
            message: `Failed to delete page preset. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          getPagePresets();
        }
      })(),

    [getPagePresets, setError]
  );

  const deleteTablePreset = useCallback(
    (params: DeleteTablePresetParams) =>
      (async () => {
        try {
          setFetchingTablePresets(true);
          const res = await SingleChannelLambdaFetch<DeleteTablePresetParams>(
            '/metrics_table_preset',
            {
              method: 'DELETE',
              params
            }
          );
          await awaitJSON(res);
        } catch (e) {
          setError({
            message: `Failed to delete table preset. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          getTablePresets();
        }
      })(),
    [getTablePresets, setError]
  );

  const getDeliveryData = useCallback(
    params =>
      (async () => {
        const paramsToUse = {
          ...params,
          filterState: params.filterState ? JSON.stringify(params.filterState) : undefined,
          sources: [...selectedSources].sort().join(',')
        };
        const res = await SingleChannelLambdaFetch('/getChannelDelivery', {
          params: paramsToUse
        });
        return awaitJSON(res);
      })(),
    [selectedSources]
  );

  const getPerformanceData = useCallback(
    params =>
      (async () => {
        const dateRangeToUse = overrideDateRange(company, params.dates);
        const paramsToUse = {
          ...params,
          ...(dateRangeToUse || {}),
          channel,
          sources: [...selectedSources].sort().join(',')
        };
        const res = await SingleChannelLambdaFetch('/getChannelMetricsTableData', {
          params: paramsToUse
        });
        return awaitJSON<GetPageDataResponse>(res);
      })(),
    [company, channel, selectedSources]
  );

  const updateSettings = useCallback(args => forceAmazonKpiSettings(company, args), [company]);

  useEffect(() => {
    if (company && !pagePresets) {
      getPagePresets();
    }
  }, [company, getPagePresets, pagePresets, setError]);

  useEffect(() => {
    if (company && !tablePresets) {
      getTablePresets();
    }
  }, [company, getTablePresets, pagePresets, setError, tablePresets]);

  useEffect(() => {
    if (company && !filterData) {
      (async () => {
        try {
          const res = await SingleChannelLambdaFetch('/getChannelFilterSourceOptions', {
            params: {
              company,
              channel,
              pageID: channel
            } as any
          });

          const [filterOptions, fetchedPlatformOptions, fetchedSourceOptions] =
            await parseBase64GzippedResponse<GetFilterSourceResponse>(res);
          setFilterData(filterOptions);
          setPlatformOptions(fetchedPlatformOptions);
          setSourceOptions(fetchedSourceOptions);
        } catch (e) {
          setError({
            message: e.message,
            reportError: e
          });
        }
      })();
    }
  }, [company, filterData, setError, channel]);

  return pagePresets && tablePresets ? (
    <SingleChannelPage
      columnMetaDataMap={columnMetaDataMap}
      dataFetchKey={header => `${header.kpi}_${header.lag}`}
      dates={dates as any}
      defaultSparkChartMetrics={getDefaultSparkChartMetrics(channel)}
      defaultCompareMetrics={getDefaultCompareMetrics(channel)}
      defaultDeliveryOverviewMetrics={getDefaultDeliveryOverviewMetrics(channel)}
      deletePagePreset={deletePagePreset}
      deleteTablePreset={deleteTablePreset}
      deliveryDimensionMap={deliveryDimensionMap}
      deliveryMetricOptions={getDeliveryPageMetricColumns(channel).map(col => ({
        label: col.prettyName,
        isSpend: col.name === 'spend',
        value: col.name
      }))}
      dimensionColumnMetaDataMap={dimensionColumnMetaDataMap}
      fetchingPagePresets={fetchingPagePresets}
      fetchingTablePresets={fetchingTablePresets}
      filterData={R.defaultTo([], filterData)}
      getDeliveryData={getDeliveryData}
      getDimensionCell={getDimensionCellWithContext}
      getPerformanceData={getPerformanceData}
      getTablePreset={getTablePreset}
      glossary={glossary}
      isGraph={isGraph}
      kpiMetaData={{}}
      pagePresets={R.defaultTo([], pagePresets)}
      analyticsIsSource={analyticsIsSource}
      platformOptions={platformOptions}
      prefix={channel}
      savePagePreset={savePagePreset}
      saveTablePreset={saveTablePreset}
      selectedSources={selectedSources}
      setSelectedSources={setSelectedSources}
      setDates={setDates as any}
      setPagePresets={setPagePresets}
      setTablePresets={setTablePresets}
      sourceOptions={sourceOptions}
      sparkChartMetricsMap={sparkMetrics}
      sparkChartMetricPrettyNameMap={sparkPrettyNames}
      tablePresets={R.defaultTo([], tablePresets)}
      updateSettings={updateSettings}
      useCrossChannelKpis
    />
  ) : (
    <FullPageSpinner />
  );
};

// Thin per-channel bindings so each can be mounted as a props-less route component (the routing
// config needs a ComponentType).
export const SocialSingleChannel = () => <SingleChannelFactory channel={SOCIAL} />;
export const SearchShoppingSingleChannel = () => <SingleChannelFactory channel={SEARCHSHOPPING} />;
export const ProgrammaticSingleChannel = () => <SingleChannelFactory channel={PROGRAMMATIC} />;
export const YoutubeSingleChannel = () => <SingleChannelFactory channel={YOUTUBE} />;
export const EmailSingleChannel = () => <SingleChannelFactory channel={EMAIL} />;
export const CommerceSingleChannel = () => <SingleChannelFactory channel={COMMERCE} />;
export const StreamingSingleChannel = () => (
  <SingleChannelFactory
    channel={STREAMING}
    isGraph={!useExperimentFlag('showIPStreamingPerformance')}
  />
);
export const AudioSingleChannel = () => (
  <SingleChannelFactory channel={AUDIO} isGraph={!useExperimentFlag('showIPAudioPerformance')} />
);
export const DisplaySingleChannel = () => (
  <SingleChannelFactory
    channel={DISPLAY}
    isGraph={!useExperimentFlag('showIPDisplayPerformance')}
  />
);
export const LinearSingleChannel = () => <SingleChannelFactory channel={LINEAR} />;
export const RadioSingleChannel = () => <SingleChannelFactory channel={RADIO} />;
